/**
 * FE-only signal for "an error boundary is currently showing its fallback".
 *
 * `AppErrorBoundary` replaces the page it wraps, but app-shell-level surfaces
 * mounted as its siblings — the Sofia panel above all — keep rendering over the
 * error screen. Those surfaces sit ABOVE the boundary in the tree, so they
 * cannot read its state through context; this shared singleton carries it
 * upward instead.
 *
 * A shared singleton rather than a direct import so neither side depends on the
 * other's module: `src/shared` cannot import app-covi, and app-covi must not
 * reach into the boundary's internals. Mirrors `coviSqlHandoff.ts`.
 */

import { useSyncExternalStore } from "react"

// A count, not a boolean: the app and route boundaries are nested, and both can
// be showing a fallback at once. The outer one must not be cleared by the inner
// one recovering.
let shownCount = 0
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function getSnapshot(): boolean {
  return shownCount > 0
}

// The server never renders a fallback: boundaries catch client render errors
// only, so hydration must start from "no error".
function getServerSnapshot(): boolean {
  return false
}

/**
 * Marks a fallback as on screen and returns the cleanup that clears it, so the
 * signal is tied to the fallback's own mount rather than to a separate reset.
 */
export function markErrorFallbackShown(): () => void {
  shownCount += 1
  emit()
  let released = false
  return () => {
    if (released) {
      return
    }
    released = true
    shownCount -= 1
    emit()
  }
}

/** True while any `AppErrorBoundary` is rendering its fallback. */
export function useErrorFallbackShown(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
